import React, { useEffect, useState } from 'react';
import { getLscVodSyncReadiness, syncLscVods } from '../api';
import type { LscSyncRunSummary } from '../types';
import { parseLscMutationError, type ParsedLscMutationError } from '../entitlementBillingErrors';
import LscMutationErrorAlert from './LscMutationErrorAlert';
import VodSyncReadinessPanel from './VodSyncReadinessPanel';

interface VodSyncPanelProps {
  leagueId: string;
  onSynced?: () => void;
}

const VodSyncPanel: React.FC<VodSyncPanelProps> = ({ leagueId, onSynced }) => {
  const [dryRun, setDryRun] = useState(true);
  const [manualApplyEnabled, setManualApplyEnabled] = useState(false);
  const [createdAfter, setCreatedAfter] = useState('');
  const [maxPerMember, setMaxPerMember] = useState('100');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ParsedLscMutationError | null>(null);
  const [summary, setSummary] = useState<LscSyncRunSummary | null>(null);
  const [readinessRefreshKey, setReadinessRefreshKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const readiness = await getLscVodSyncReadiness(leagueId);
        if (!cancelled) {
          const enabled = Boolean(readiness.manual_apply_enabled);
          setManualApplyEnabled(enabled);
          if (!enabled) setDryRun(true);
        }
      } catch {
        if (!cancelled) {
          setManualApplyEnabled(false);
          setDryRun(true);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [leagueId, readinessRefreshKey]);

  const handleSync = async () => {
    if (
      !dryRun &&
      !window.confirm(
        'Apply VOD sync will create or update Twitch VOD candidates in your review queue. Continue?'
      )
    ) {
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const result = await syncLscVods(leagueId, {
        dry_run: dryRun,
        created_after: createdAfter || undefined,
        max_per_member: parseInt(maxPerMember, 10) || 100,
      });
      setSummary(result);
      setReadinessRefreshKey((value) => value + 1);
      if (!dryRun) onSynced?.();
    } catch (err) {
      setError(parseLscMutationError(err, 'VOD sync failed.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <VodSyncReadinessPanel leagueId={leagueId} refreshKey={readinessRefreshKey} />

      <div className="card max-w-lsc-narrow space-y-4" data-testid="vod-sync-panel">
        <div>
          <h2 className="text-lg font-semibold text-white mb-1">Sync Twitch VOD candidates</h2>
          <p className="text-sm text-secondary-400">
            Metadata-only sync from linked stream accounts. No video downloads.
          </p>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="block text-sm text-secondary-300 mb-1" htmlFor="created-after">
              Created after (optional)
            </label>
            <input
              id="created-after"
              type="datetime-local"
              className="input-field"
              value={createdAfter}
              onChange={(e) => setCreatedAfter(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm text-secondary-300 mb-1" htmlFor="max-per-member">
              Max per member
            </label>
            <input
              id="max-per-member"
              type="number"
              min={1}
              max={500}
              className="input-field"
              value={maxPerMember}
              onChange={(e) => setMaxPerMember(e.target.value)}
            />
          </div>
        </div>

        <label className="flex items-center gap-2 text-sm text-secondary-300">
          <input
            type="checkbox"
            checked={dryRun}
            onChange={(e) => setDryRun(e.target.checked)}
            data-testid="vod-sync-dry-run-toggle"
            disabled={!manualApplyEnabled}
          />
          Preview mode (recommended)
        </label>

        {!manualApplyEnabled && (
          <p className="text-sm text-secondary-500" data-testid="vod-sync-apply-disabled-note">
            Saving to the review queue isn't included for this league. Preview still works. If this
            league should be able to save, contact support.
          </p>
        )}

        {manualApplyEnabled && !dryRun && (
          <p className="text-sm text-amber-300" data-testid="vod-sync-apply-warning">
            Save for review will add VOD items to your review queue. Use preview mode unless you
            intend to write.
          </p>
        )}

        <button
          type="button"
          className="btn-primary"
          onClick={handleSync}
          disabled={loading || (!dryRun && !manualApplyEnabled)}
        >
          {loading ? 'Syncing...' : dryRun ? 'Preview VOD sync' : 'Add to review queue'}
        </button>

        <LscMutationErrorAlert error={error} onDismiss={() => setError(null)} />

        {summary && (
          <div
            className="rounded-lg bg-secondary-900/60 border border-secondary-700 p-4 text-sm space-y-2"
            data-testid="vod-sync-summary"
          >
            <p className="text-secondary-200 font-medium">Sync summary</p>
            {summary.status && <p>Status: {summary.status}</p>}
            {summary.members_scanned != null && (
              <p>Members scanned: {summary.members_scanned}</p>
            )}
            {summary.members_skipped != null && (
              <p>Members skipped: {summary.members_skipped}</p>
            )}
            {summary.vods_seen != null && <p>VODs seen: {summary.vods_seen}</p>}
            {summary.vods_created != null && <p>Candidates created: {summary.vods_created}</p>}
            {summary.vods_updated != null && <p>Candidates updated: {summary.vods_updated}</p>}
            {summary.dry_run && <p className="text-amber-300">Preview mode — no changes saved.</p>}
            {summary.warnings && summary.warnings.length > 0 && (
              <div data-testid="vod-sync-warnings">
                <p className="text-secondary-300 font-medium mt-2">Warnings</p>
                <ul className="list-disc pl-5 text-secondary-400">
                  {summary.warnings.map((warning, index) => (
                    <li key={`${warning.code ?? 'warning'}-${index}`}>
                      {warning.message?.trim() ||
                        'A warning occurred during sync. Try again or contact support.'}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {summary.errors && summary.errors.length > 0 && (
              <div data-testid="vod-sync-errors">
                <p className="text-secondary-300 font-medium mt-2">Errors</p>
                <ul className="list-disc pl-5 text-secondary-400">
                  {summary.errors.map((entry, index) => (
                    <li key={`${entry.code ?? 'error'}-${index}`}>
                      {entry.message?.trim() ||
                        'Sync hit a problem. Try again or contact support.'}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default VodSyncPanel;
